import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';

export interface MedidaSetDTO {
  id: string;
  project_id: string;
  nombre: string;
  descripcion: string | null;
  medidas_count: number;
  proformas_count: number;
  created_at: string;
  created_by: { id: string; nombre: string };
}

// ── List ──

export function useMedidaSets(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.medidaSets.list(projectId ?? ''),
    queryFn: async () => {
      const response = await api.get<{ data: MedidaSetDTO[] }>(`/projects/${projectId}/medida-sets`);
      return response.data.data;
    },
    enabled: !!projectId,
  });
}

// ── Create ──

export function useCreateMedidaSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { projectId: string; nombre: string; descripcion?: string | null; silent?: boolean }) => {
      const response = await api.post<{ data: MedidaSetDTO }>(
        `/projects/${vars.projectId}/medida-sets`,
        { nombre: vars.nombre, descripcion: vars.descripcion ?? null },
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidaSets.list(variables.projectId) });
      if (!variables.silent) {
        toast('success', 'Registro de medidas creado');
      }
    },
  });
}

// ── Rename ──

export function useRenameMedidaSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { projectId: string; setId: string; nombre: string }) => {
      const response = await api.put<{ data: MedidaSetDTO }>(
        `/medida-sets/${vars.setId}`,
        { nombre: vars.nombre },
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidaSets.list(variables.projectId) });
      toast('success', 'Set renombrado');
    },
  });
}

// ── Delete ──

export function useDeleteMedidaSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { projectId: string; setId: string; silent?: boolean }) => {
      await api.delete(`/medida-sets/${vars.setId}`);
    },
    onMutate: async (variables) => {
      // Cancel in-flight refetches so they don't overwrite the optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.medidaSets.list(variables.projectId) });
      // Snapshot for rollback
      const previousSets = queryClient.getQueryData<MedidaSetDTO[]>(
        queryKeys.medidaSets.list(variables.projectId),
      );
      // Immediately remove the set from cache — card disappears without waiting for the server
      queryClient.setQueryData<MedidaSetDTO[]>(
        queryKeys.medidaSets.list(variables.projectId),
        (old) => old?.filter((s) => s.id !== variables.setId) ?? [],
      );
      return { previousSets };
    },
    onSuccess: (_data, variables) => {
      // Re-sync with server to confirm
      queryClient.invalidateQueries({ queryKey: queryKeys.medidaSets.list(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: ['medidas', 'list', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-summary', variables.projectId] });
      if (!variables.silent) {
        toast('success', 'Set eliminado');
      }
    },
    onError: (_err, variables, context) => {
      // Roll back the optimistic removal
      if (context?.previousSets !== undefined) {
        queryClient.setQueryData(
          queryKeys.medidaSets.list(variables.projectId),
          context.previousSets,
        );
      }
      toast('error', 'No se puede eliminar un set con proformas asociadas');
    },
  });
}

// ── Clone ──

export function useCloneMedidaSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { projectId: string; setId: string; nombre: string }) => {
      const response = await api.post<{ data: MedidaSetDTO }>(
        `/medida-sets/${vars.setId}/clone`,
        { nombre: vars.nombre },
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.medidaSets.list(variables.projectId) });
      toast('success', 'Set clonado');
    },
  });
}
